// Copied from kettle-platform workers/admin/ui/src/charts/Lines.tsx (2026-10-30); restyled for Estra, see docs/kettle-sharing.md.
// Multi-series daily line chart: one line per project (or period), colour fixed per project
// (the order projects were created, never by rank), 2px lines, recessive grid,
// monotone curves (smooth, never overshooting a day's value or dipping below zero) over a
// faint fade-to-baseline fill, crosshair + tooltip on hover/focus, legend that focuses a line, direct end labels when
// there are four or fewer, and a table view of the same numbers.
import { createContext, useContext, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import { niceMax, runs, shortDay, smooth } from "./scale";

export { runs, smooth } from "./scale";

/** A moment worth marking on a chart (a milestone, a config or content release, a deploy). */
export interface Mark {
  day: string;
  label: string;
  kind?: "milestone" | "release" | "deploy";
}

/** The project's milestones, so every daily chart marks them without fetching (the project layout provides it). */
export const MarksContext = createContext<Mark[]>([]);

export interface Series {
  id: string;
  name: string;
  values: (number | null)[];
  color: string;
  /** Drawn dashed (a reference line, e.g. a week earlier). */
  dash?: boolean;
}

/** The drawing size before the chart has measured its box (it then draws at its real width, so text stays its true size). */
const W0 = 960;

export function Lines({ days, series, format, label, labels, desc, group = "Projects", marks: own }: {
  days: string[];
  series: Series[];
  format: (n: number) => string;
  label: string;
  /** X labels in place of dates (e.g. clock hours); `days` then only keys the points. */
  labels?: string[];
  desc?: string;
  group?: string;
  /** Vertical markers on the days they happened (drawn under the lines, named in the tooltip). */
  marks?: Mark[];
}) {
  const shared = useContext(MarksContext);
  const marks = own ?? shared;
  const lab = (i: number) => labels?.[i] ?? shortDay(days[i]!);
  const id = useId();
  const gid = `g${id.replace(/[^\w-]/g, "")}`; // url(#…) needs a plain id
  const [hover, setHover] = useState<number | null>(null);
  const [focus, setFocus] = useState<string | null>(null);
  const [asTable, setAsTable] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const [W, setW] = useState(W0);
  useEffect(() => {
    const el = box.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([e]) => e && e.contentRect.width > 0 && setW(Math.max(280, Math.round(e.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, [asTable]);
  const H = Math.round(Math.min(300, Math.max(190, W * 0.32)));
  const labelled = series.length <= 4;
  const PAD = { l: 48, r: labelled ? 112 : 12, t: 14, b: 28 };
  const max = useMemo(() => niceMax(Math.max(0, ...series.flatMap((s) => s.values.map((v) => v ?? 0)))), [series]);
  const n = Math.max(days.length, 2);
  const x = (i: number) => PAD.l + ((W - PAD.l - PAD.r) * i) / (n - 1);
  const y = (v: number) => PAD.t + (H - PAD.t - PAD.b) * (1 - v / max);
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * max);
  const xticks = days.length ? [0, Math.round((days.length - 1) / 3), Math.round(((days.length - 1) * 2) / 3), days.length - 1] : [];

  const path = (vals: (number | null)[]) => runs(vals, x, y).map(smooth).join("");
  const area = (vals: (number | null)[]) =>
    runs(vals, x, y)
      .filter((r) => r.length > 1)
      .map((r) => `${smooth(r)}L${r[r.length - 1]![0].toFixed(1)} ${y(0).toFixed(1)}L${r[0]![0].toFixed(1)} ${y(0).toFixed(1)}Z`)
      .join("");

  // End labels: each line's last value, nudged apart so names never overlap.
  const ends = useMemo(() => {
    if (!labelled) return [];
    const out = series
      .map((s) => {
        let i = s.values.length - 1;
        while (i >= 0 && s.values[i] === null) i--;
        return i < 0 ? null : { s, i, yy: y(s.values[i]!) };
      })
      .filter((e): e is { s: Series; i: number; yy: number } => !!e)
      .sort((a, b) => a.yy - b.yy);
    for (let k = 1; k < out.length; k++) if (out[k]!.yy - out[k - 1]!.yy < 14) out[k]!.yy = out[k - 1]!.yy + 14;
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series, max, labelled, H]);

  const pick = (e: PointerEvent<SVGRectElement>) => {
    const r = (e.currentTarget.ownerSVGElement as SVGSVGElement).getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * W;
    setHover(Math.max(0, Math.min(days.length - 1, Math.round(((px - PAD.l) / (W - PAD.l - PAD.r)) * (n - 1)))));
  };
  const keys = (e: KeyboardEvent) => {
    if (e.key === "ArrowLeft") setHover((h) => Math.max(0, (h ?? days.length) - 1));
    else if (e.key === "ArrowRight") setHover((h) => Math.min(days.length - 1, (h ?? -1) + 1));
    else if (e.key === "Escape") setHover(null);
    else return;
    e.preventDefault();
  };

  const tipLeft = hover === null ? 0 : (x(hover) / W) * 100;
  const rows = hover === null ? [] : series.map((s) => ({ s, v: s.values[hover] })).sort((a, b) => (b.v ?? -1) - (a.v ?? -1));

  return (
    <div className={focus ? "lines focus" : "lines"}>
      {asTable ? (
        <div className="table-wrap" style={{ maxHeight: 340, marginTop: "1rem" }}>
          <table>
            <thead><tr><th>Day</th>{series.map((s) => <th key={s.id} className="num">{s.name}</th>)}</tr></thead>
            <tbody>
              {[...days.keys()].reverse().map((i) => (
                <tr key={days[i]}><td className="mono">{labels?.[i] ?? days[i]}</td>{series.map((s) => <td key={s.id} className="num">{s.values[i] === null ? "–" : format(s.values[i]!)}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div ref={box} style={{ position: "relative" }}>
          <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-labelledby={`${id}-d`} tabIndex={0} onKeyDown={keys} onBlur={() => setHover(null)}>
            <desc id={`${id}-d`}>{desc ?? `${label} per day, ${days[0] ?? ""} to ${days[days.length - 1] ?? ""}. Use the table view for exact numbers.`}</desc>
            <g className="chart">
              {ticks.map((t, k) => (
                <g key={k}>
                  <line className={k === 0 ? "baseline" : "grid"} x1={PAD.l} x2={W - PAD.r} y1={y(t)} y2={y(t)} />
                  <text className="axis" x={PAD.l - 10} y={y(t) + 3} textAnchor="end">{format(t)}</text>
                </g>
              ))}
              {xticks.map((i) => (
                <text key={i} className="axis" x={x(i)} y={H - 8} textAnchor={i === 0 ? "start" : i === days.length - 1 ? "end" : "middle"}>{lab(i)}</text>
              ))}
            </g>
            <defs>
              {series.map((s, k) => (
                <linearGradient key={s.id} id={`${gid}-${k}`} gradientUnits="userSpaceOnUse" x1={0} x2={0} y1={PAD.t} y2={y(0)}>
                  <stop offset="0" stopColor={s.color} stopOpacity={0.22} />
                  <stop offset="1" stopColor={s.color} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            {marks.map((m) => {
              const i = days.indexOf(m.day);
              if (i < 0) return null;
              return (
                <g key={`${m.day}-${m.label}`} className={`chart-mark ${m.kind ?? "release"}`}>
                  <line x1={x(i)} x2={x(i)} y1={PAD.t + 10} y2={H - PAD.b} />
                  <path d={`M${x(i)} ${PAD.t - 1}l5 5-5 5-5-5z`} />
                  <title>{`${lab(i)}: ${m.label}`}</title>
                </g>
              );
            })}
            {series.map((s, k) => (
              <path key={s.id} className={focus === s.id ? "area on" : "area"} d={area(s.values)} fill={`url(#${gid}-${k})`} />
            ))}
            {series.map((s) => (
              <path key={s.id} className={focus === s.id ? "series on" : "series"} d={path(s.values)} stroke={s.color} strokeDasharray={s.dash ? "5 5" : undefined} />
            ))}
            {ends.map((e) => (
              <g key={e.s.id} opacity={focus && focus !== e.s.id ? 0.25 : 1}>
                <circle cx={x(e.i)} cy={y(e.s.values[e.i]!)} r={4} fill={e.s.color} className="pt" />
                <text className="end-label" x={x(e.i) + 10} y={e.yy + 4}>{e.s.name.length > 14 ? `${e.s.name.slice(0, 13)}…` : e.s.name}</text>
              </g>
            ))}
            {hover !== null && (
              <g>
                <line className="cross" x1={x(hover)} x2={x(hover)} y1={PAD.t} y2={H - PAD.b} />
                {series.map((s) => (s.values[hover] === null ? null : <circle key={s.id} className="pt" cx={x(hover)} cy={y(s.values[hover]!)} r={4.5} fill={s.color} />))}
              </g>
            )}
            <rect x={PAD.l} y={0} width={W - PAD.l - PAD.r} height={H - PAD.b} fill="transparent" onPointerMove={pick} onPointerDown={pick} onPointerLeave={() => setHover(null)} />
          </svg>
          {hover !== null && (
            <div className="tip" role="status" style={{ left: `clamp(0px, calc(${tipLeft}% - 5.5rem), calc(100% - 11rem))`, top: 8 }}>
              <div className="tip-day">{lab(hover)}</div>
              {marks.filter((m) => m.day === days[hover]).map((m) => <div key={m.label} className="tip-mark">◆ {m.label}</div>)}
              {rows.map(({ s, v }) => (
                <div key={s.id}>
                  <span><i style={{ background: s.color }} />{s.name}</span>
                  <b>{v === null || v === undefined ? "–" : format(v)}</b>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="legend" role="group" aria-label={group}>
        {series.map((s) => {
          const last = [...s.values].reverse().find((v) => v !== null);
          return (
            <button key={s.id} className={focus === s.id ? "on" : ""} onMouseEnter={() => setFocus(s.id)} onMouseLeave={() => setFocus(null)} onFocus={() => setFocus(s.id)} onBlur={() => setFocus(null)} aria-label={`${s.name}: ${last === undefined ? "no data" : format(last)} latest`}>
              <i style={{ background: s.color }} />
              {s.name}
              <b>{last === undefined ? "–" : format(last)}</b>
            </button>
          );
        })}
        <button style={{ marginLeft: "auto" }} onClick={() => setAsTable((v) => !v)} aria-pressed={asTable}>{asTable ? "Chart" : "Table"}</button>
      </div>
    </div>
  );
}

/** A tiny trend line for a project tile (no axes; the tile shows the number). */
export function Spark({ values, color }: { values: (number | null)[]; color: string }) {
  const max = Math.max(1, ...values.map((v) => v ?? 0));
  const n = Math.max(values.length - 1, 1);
  const d = runs(values, (i) => (i / n) * 200, (v) => 26 - (v / max) * 24).map(smooth).join("");
  return (
    <svg viewBox="0 0 200 28" preserveAspectRatio="none" aria-hidden="true">
      <path d={d} fill="none" stroke={color} strokeWidth={1.5} vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}
